import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { YCN_SOCIAL } from "@/data/constants";
import { Mail, Facebook, Instagram } from "lucide-react";

export default function NewsletterSignup() {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/newsletter", { first_name: firstName, email });
      setDone(true);
      toast.success("You're on the list! Welcome to YCN.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="relative mt-24 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto ycn-glass rounded-3xl overflow-hidden relative">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full blur-3xl opacity-30 bg-violet-600" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full blur-3xl opacity-30 bg-orange-500" />
        <div className="relative p-8 md:p-14 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full ycn-glass text-[11px] uppercase tracking-[0.25em]">
              <Mail size={12} /> Stay Connected
            </div>
            <h3 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">
              Stay Connected <span className="ycn-gradient-text">to YCN</span>
            </h3>
            <p className="mt-3 text-white/70 max-w-md">
              Get updates on new releases, premieres, live events, YCN Originals, and network news.
            </p>
            <div className="mt-6" data-testid="newsletter-social-follow">
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-2">Follow us</div>
              <div className="flex items-center gap-2 flex-wrap">
                <a
                  href={YCN_SOCIAL.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="newsletter-follow-facebook"
                  aria-label="Follow YCN on Facebook"
                  className="group inline-flex items-center gap-2 px-4 py-2 rounded-full ycn-glass hover:bg-white/15 text-sm font-semibold text-white/85 hover:text-white transition"
                >
                  <span className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-600 via-blue-600 to-orange-500 flex items-center justify-center">
                    <Facebook size={13} className="text-white" fill="currentColor" />
                  </span>
                  Facebook
                </a>
                <a
                  href={YCN_SOCIAL.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="newsletter-follow-instagram"
                  aria-label="Follow YCN on Instagram"
                  className="group inline-flex items-center gap-2 px-4 py-2 rounded-full ycn-glass hover:bg-white/15 text-sm font-semibold text-white/85 hover:text-white transition"
                >
                  <span className="h-7 w-7 rounded-full bg-gradient-to-br from-violet-600 via-blue-600 to-orange-500 flex items-center justify-center">
                    <Instagram size={13} className="text-white" />
                  </span>
                  Instagram
                </a>
              </div>
            </div>
          </div>
          {done ? (
            <div className="p-6 rounded-2xl ycn-glass text-center" data-testid="newsletter-success">
              <p className="font-display text-xl font-bold ycn-gradient-text">You're subscribed.</p>
              <p className="text-white/70 text-sm mt-1">We'll be in touch soon.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <input
                  data-testid="newsletter-firstname-input"
                  required minLength={1} value={firstName} onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition"
                />
                <input
                  data-testid="newsletter-email-input"
                  required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  className="w-full rounded-full bg-white/5 border border-white/15 focus:border-white/40 focus:bg-white/10 outline-none px-5 py-3 text-white placeholder:text-white/40 transition"
                />
              </div>
              <button data-testid="newsletter-submit-btn" disabled={loading}
                className="ycn-btn-primary w-full py-3 rounded-full font-bold text-white disabled:opacity-60 transition-all">
                {loading ? "Subscribing…" : "Subscribe"}
              </button>
              <p className="text-[11px] text-white/40 text-center">By subscribing you agree to receive updates from YCN. Unsubscribe anytime.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
